import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { useContext } from 'react';
import isArray from 'lodash/isArray';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useIsCurrentUserDeviationAuthor } from '../../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { useDeviationAttributes } from '../../../../DeviationViews/hooks/useDeviationAttributes';
import { useSubscriptionTier } from '../../../../utils/hooks/useSubscriptionTier';
import { WindowBreakpoint } from '../../types';
import { StandardDeviationElementPlacement, NOT_INCLUDED_VISIBILITY_GROUP } from './types';
import { getDeviationType, isSimpleDeviation } from '../../../../DeviationViews/_helpers';
import { DeviationTypes } from '../../../../types/deviation';
import { getAdoptableBuyerElementGroupBase } from '../../sharedBases/adoptableBuyer';
import { getAdoptableSellerElementGroupBase } from '../../sharedBases/adoptableSeller';
import { useWindowBreakpoint } from '../../utils';
import { useEnabledFlagList } from '@wix/da-react-context/pkg/flags/hooks';
/**
 * A set of common checks against the adoptable element groups that other controls use to collapse.
 */ function shouldCollapseForAdoptableElementGroups(determinationContext) {
    const { elementGroups: { adoptableBuyer, adoptableSeller }, windowBreakpoint } = determinationContext;
    return(// If the adoptable buyer group has a couple elements, there isn't enough room. Signal that we should collapse this control.
    adoptableBuyer.groupPlacement === StandardDeviationElementPlacement.PrimaryBar && adoptableBuyer.countOnBar >= 2 || // On smaller desktop screens, we'll signal to collapse this control if either adoptable-related element groups are active. Regardless
    // of the number of elements visible from each.
    (adoptableBuyer.groupPlacement === StandardDeviationElementPlacement.PrimaryBar || adoptableSeller.groupPlacement === StandardDeviationElementPlacement.PrimaryBar) && windowBreakpoint === WindowBreakpoint.DesktopSmallSize);
}
function getEditBackgroundPlacement(determinationContext) {
    return determinationContext.deviationAttributes.isBackgroundEditable ? StandardDeviationElementPlacement.PrimaryBar : StandardDeviationElementPlacement.NotIncluded;
}
/**
 * Will return a boolean indicating whether the provided menu {@link targetPlacement} is included in the placement map.
 *
 * This is useful for determining if we should include the more menu buttons at all. Prevents downstream consumers from
 * having to do it themselves.
 */ function hasElementInTargetMoreMenu(elementPlacementMap, targetPlacement) {
    const elementPlacementValues = Object.values(elementPlacementMap);
    for(let i = 0; i < elementPlacementValues.length; i++){
        const currentPlacementValue = elementPlacementValues[i];
        if (isArray(currentPlacementValue) && currentPlacementValue.includes(targetPlacement)) {
            return true;
        } else if (currentPlacementValue === targetPlacement) {
            return true;
        }
    }
    return false;
}
/**
 * Returns an object that maps ActionBar elements to their {@link StandardDeviationElementPlacement}.
 * (e.g. is this element on the bar? collapsed into the more menu? completely absent?)
 */ export function useStandardDeviationElementPlacementMap(deviation) {
    var _deviation_extended_adoptable, _deviation_extended;
    const flags = useEnabledFlagList();
    const windowBreakpoint = useWindowBreakpoint();
    const isAuthor = useIsCurrentUserDeviationAuthor(deviation);
    const isMobile = useContext(MobileContext);
    const { isLoggedIn } = useContext(SessionContext);
    const isOwner = !!((_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_adoptable = _deviation_extended.adoptable) === null || _deviation_extended_adoptable === void 0 ? void 0 : _deviation_extended_adoptable.isOwner);
    const deviationAttributes = useDeviationAttributes(deviation, isAuthor);
    // These are collections of buttons. They can take up large amounts of space on the action bar,
    // so we use this object to determine if other more general controls should be removed/collapsed.
    const elementGroups = {
        adoptableBuyer: buildElementGroup(getAdoptableBuyerElementGroupBase(deviation, isMobile, isOwner), isMobile),
        adoptableSeller: buildElementGroup(getAdoptableSellerElementGroupBase(deviation, isMobile, isOwner), isMobile)
    };
    const determinationContext = {
        deviation,
        isAuthor,
        isMobile,
        isLoggedIn,
        deviationAttributes,
        windowBreakpoint,
        elementGroups,
        flags
    };
    const intermediaryMapping = {
        comment: getCommentPlacement(determinationContext),
        premiumDownload: getPremiumDownloadPlacement(determinationContext),
        freeDownload: getFreeDownloadPlacement(determinationContext),
        addToGroup: getAddToGroupPlacement(determinationContext),
        awardBadge: getAwardBadgePlacement(determinationContext),
        favourite: getFavouritePlacement(determinationContext),
        privateCollection: getPrivateCollectionPlacement(determinationContext),
        boostDeviation: getBoostDeviationPlacement(determinationContext),
        sellDeviation: getSellDeviationPlacement(determinationContext),
        subscribe: useSubscribePlacement(determinationContext),
        editBackground: getEditBackgroundPlacement(determinationContext),
        deleteDeviation: getDeleteDeviationPlacement(determinationContext),
        editDeviation: getEditDeviationPlacement(determinationContext),
        commissionStatus: getCommissionStatusPlacement(determinationContext),
        orderCommission: getOrderCommissionPlacement(determinationContext),
        share: getSharePlacement(determinationContext),
        report: getReportPlacement(determinationContext),
        fullscreen: getFullscreenPlacement(determinationContext),
        elementGroups: elementGroups,
        viewOrderHistory: getViewOrderHistoryPlacement(determinationContext),
        unpublish: getUnpublishPlacement(determinationContext),
        meta: {
            hasPrimaryMoreActionsMenu: false,
            hasSecondaryMoreActionsMenu: false
        }
    };
    intermediaryMapping.meta = {
        // ElementPlacementMap.elementGroups has no controls that appear in menus for the time being, so we're
        // only testing the root values of elementPlacementMap.
        hasPrimaryMoreActionsMenu: hasElementInTargetMoreMenu(intermediaryMapping, StandardDeviationElementPlacement.PrimaryBarMenu),
        // ElementPlacementMap.elementGroups has no controls that appear in menus for the time being, so we're
        // only testing the root values of ElementPlacementMap.
        hasSecondaryMoreActionsMenu: hasElementInTargetMoreMenu(intermediaryMapping, StandardDeviationElementPlacement.SecondaryBarMenu)
    };
    return intermediaryMapping;
}
function getDeleteDeviationPlacement(determinationContext) {
    return determinationContext.deviationAttributes.isDeletable ? StandardDeviationElementPlacement.PrimaryBar : StandardDeviationElementPlacement.NotIncluded;
}
function getUnpublishPlacement(determinationContext) {
    const { deviation, isAuthor } = determinationContext;
    const isTier = getDeviationType(deviation) === DeviationTypes.TIER;
    const isSimple = isSimpleDeviation(deviation);
    if (determinationContext.flags.includes('unpublish') && !isSimple && !isTier && isAuthor) {
        return StandardDeviationElementPlacement.PrimaryBarMenu;
    }
    return StandardDeviationElementPlacement.NotIncluded;
}
function getEditDeviationPlacement(determinationContext) {
    // For now, the tier edit popup doesn't work on mobile
    if (determinationContext.isMobile && getDeviationType(determinationContext.deviation) === DeviationTypes.TIER) {
        return StandardDeviationElementPlacement.NotIncluded;
    }
    return determinationContext.deviationAttributes.isEditable ? StandardDeviationElementPlacement.PrimaryBar : StandardDeviationElementPlacement.NotIncluded;
}
function getCommissionStatusPlacement(determinationContext) {
    const deviationType = getDeviationType(determinationContext.deviation);
    if (deviationType === DeviationTypes.COMMISSION && determinationContext.isMobile && determinationContext.deviationAttributes.isEditable) {
        return StandardDeviationElementPlacement.PrimaryBar;
    }
    return StandardDeviationElementPlacement.NotIncluded;
}
function getOrderCommissionPlacement(determinationContext) {
    const deviationType = getDeviationType(determinationContext.deviation);
    if (deviationType === DeviationTypes.COMMISSION && determinationContext.isMobile && !determinationContext.isAuthor && determinationContext.deviationAttributes.isAuthorCommissionable) {
        return StandardDeviationElementPlacement.PrimaryBar;
    }
    return StandardDeviationElementPlacement.NotIncluded;
}
function getViewOrderHistoryPlacement(determinationContext) {
    const { deviationAttributes, isAuthor } = determinationContext;
    // Only show the "View order history" link to owner
    if (deviationAttributes.isPurchasable && isAuthor) {
        return StandardDeviationElementPlacement.PrimaryBarMenu;
    }
    return StandardDeviationElementPlacement.NotIncluded;
}
function getCommentPlacement(determinationContext) {
    const { deviation, deviationAttributes: { isCommentable }, elementGroups } = determinationContext;
    const deviationType = getDeviationType(deviation);
    const isJournal = deviationType === DeviationTypes.JOURNAL;
    if (!isCommentable) {
        return StandardDeviationElementPlacement.NotIncluded;
    }
    if (shouldCollapseForAdoptableElementGroups(determinationContext) || // In addition to the common checks provided by shouldCollapseForAdoptableElementGroups,
    // we want to collapse this control in all cases the adoptableSeller group is on the primary bar.
    elementGroups.adoptableSeller.groupPlacement === StandardDeviationElementPlacement.PrimaryBar) {
        return isJournal ? [
            StandardDeviationElementPlacement.PrimaryBarMenu,
            StandardDeviationElementPlacement.SecondaryBarMenu
        ] : [
            StandardDeviationElementPlacement.PrimaryBarMenu
        ];
    }
    return isJournal ? [
        StandardDeviationElementPlacement.PrimaryBar,
        StandardDeviationElementPlacement.SecondaryBar
    ] : [
        StandardDeviationElementPlacement.PrimaryBar
    ];
}
function getFreeDownloadPlacement(determinationContext) {
    const { isLoggedIn, isMobile, deviationAttributes: { isDownloadable, isAdoptable } } = determinationContext;
    if (!isDownloadable) {
        return StandardDeviationElementPlacement.NotIncluded;
    }
    if (shouldCollapseForAdoptableElementGroups(determinationContext)) {
        return StandardDeviationElementPlacement.PrimaryBarMenu;
    }
    if (isMobile && !isLoggedIn) {
        return isAdoptable ? StandardDeviationElementPlacement.NotIncluded : StandardDeviationElementPlacement.SecondaryBar;
    }
    return StandardDeviationElementPlacement.PrimaryBar;
}
function getPremiumDownloadPlacement(determinationContext) {
    const { deviationAttributes: { isPurchasable } } = determinationContext;
    if (!isPurchasable) {
        return StandardDeviationElementPlacement.NotIncluded;
    }
    if (shouldCollapseForAdoptableElementGroups(determinationContext)) {
        return StandardDeviationElementPlacement.PrimaryBarMenu;
    }
    return StandardDeviationElementPlacement.PrimaryBar;
}
function getFavouritePlacement(determinationContext) {
    const { deviation, deviationAttributes } = determinationContext;
    const deviationType = getDeviationType(deviation);
    const isJournal = deviationType === DeviationTypes.JOURNAL;
    if (!deviationAttributes.isFavouritable) {
        return StandardDeviationElementPlacement.NotIncluded;
    }
    return isJournal ? [
        StandardDeviationElementPlacement.PrimaryBar,
        StandardDeviationElementPlacement.SecondaryBar
    ] : [
        StandardDeviationElementPlacement.PrimaryBar
    ];
}
function getAddToGroupPlacement(determinationContext) {
    const { deviation, deviationAttributes: { isGroupSubmittable }, isAuthor } = determinationContext;
    const deviationType = getDeviationType(deviation);
    const isJournal = deviationType === DeviationTypes.JOURNAL;
    if (!isGroupSubmittable) {
        return StandardDeviationElementPlacement.NotIncluded;
    }
    if (isAuthor) {
        return isJournal ? [
            StandardDeviationElementPlacement.PrimaryBar,
            StandardDeviationElementPlacement.SecondaryBar
        ] : [
            StandardDeviationElementPlacement.PrimaryBar
        ];
    }
    return isJournal ? [
        StandardDeviationElementPlacement.PrimaryBarMenu,
        StandardDeviationElementPlacement.SecondaryBarMenu
    ] : [
        StandardDeviationElementPlacement.PrimaryBarMenu
    ];
}
function getAwardBadgePlacement(determinationContext) {
    const { deviationAttributes: { isCoreBadgeAwardable } } = determinationContext;
    if (!isCoreBadgeAwardable) {
        return StandardDeviationElementPlacement.NotIncluded;
    }
    if (shouldCollapseForAdoptableElementGroups(determinationContext)) {
        return StandardDeviationElementPlacement.PrimaryBarMenu;
    }
    return StandardDeviationElementPlacement.PrimaryBar;
}
function getPrivateCollectionPlacement(determinationContext) {
    const { isAuthor } = determinationContext;
    if (isAuthor) {
        return StandardDeviationElementPlacement.NotIncluded;
    }
    if (shouldCollapseForAdoptableElementGroups(determinationContext)) {
        return StandardDeviationElementPlacement.PrimaryBarMenu;
    }
    return StandardDeviationElementPlacement.PrimaryBar;
}
function getFullscreenPlacement({ isLoggedIn }) {
    return isLoggedIn ? StandardDeviationElementPlacement.PrimaryBar : StandardDeviationElementPlacement.NotIncluded;
}
function getBoostDeviationPlacement(determinationContext) {
    var _deviation_extended;
    const { deviation } = determinationContext;
    return ((_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : _deviation_extended.isBoostable) ? StandardDeviationElementPlacement.PrimaryBar : StandardDeviationElementPlacement.NotIncluded;
}
function getSellDeviationPlacement(determinationContext) {
    const { deviationAttributes, isMobile } = determinationContext;
    if (!deviationAttributes.isSellable) {
        return StandardDeviationElementPlacement.NotIncluded;
    }
    return isMobile ? StandardDeviationElementPlacement.SecondaryBar : StandardDeviationElementPlacement.PrimaryBar;
}
function useSubscribePlacement(determinationContext) {
    const tier = useSubscriptionTier(determinationContext.deviation);
    return !determinationContext.deviationAttributes.hasFlag && !!tier ? StandardDeviationElementPlacement.PrimaryBar : StandardDeviationElementPlacement.NotIncluded;
}
function getSharePlacement(determinationContext) {
    const { deviation, deviationAttributes } = determinationContext;
    const deviationType = getDeviationType(deviation);
    const isJournal = deviationType === DeviationTypes.JOURNAL;
    if (!deviationAttributes.isShareable) {
        return StandardDeviationElementPlacement.NotIncluded;
    }
    return isJournal ? [
        StandardDeviationElementPlacement.PrimaryBarMenu,
        StandardDeviationElementPlacement.SecondaryBarMenu
    ] : [
        StandardDeviationElementPlacement.PrimaryBarMenu
    ];
}
function getReportPlacement(determinationContext) {
    const { deviation, deviationAttributes } = determinationContext;
    const deviationType = getDeviationType(deviation);
    const isJournal = deviationType === DeviationTypes.JOURNAL;
    if (!deviationAttributes.isReportable) {
        return StandardDeviationElementPlacement.NotIncluded;
    }
    return isJournal ? [
        StandardDeviationElementPlacement.PrimaryBarMenu,
        StandardDeviationElementPlacement.SecondaryBarMenu
    ] : [
        StandardDeviationElementPlacement.PrimaryBarMenu
    ];
}
function buildElementGroup(groupValues, isMobile) {
    if (!groupValues) {
        return NOT_INCLUDED_VISIBILITY_GROUP;
    }
    const countOnBar = Object.values(groupValues).filter((isVisible)=>isVisible).length;
    let groupPlacement = StandardDeviationElementPlacement.NotIncluded;
    if (countOnBar > 0) {
        groupPlacement = isMobile ? StandardDeviationElementPlacement.SecondaryBar : StandardDeviationElementPlacement.PrimaryBar;
    }
    return _object_spread({
        groupPlacement,
        countOnBar
    }, groupValues);
}

//# sourceMappingURL=useStandardDeviationElementPlacementMap.js.map